import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import * as yup from 'yup';
import {toTypedSchema} from "@vee-validate/yup";
import {type AnnouncementFormData, useAnnouncementStore} from "@/app/stores/announcement-store.ts";
import {AnnouncementDto, AnnouncementSeverity, AthumiService} from "@/api/enterprise-gateway-api.proxy.ts";
import {useForm} from "vee-validate";
import {storeToRefs} from "pinia";
import DateUtil from "@/app/shared/helpers/date-util.ts";
import {Button, Dialog, InputText, Textarea, DatePicker, Message} from 'primevue';
import AegCancelButton from '@/app/admin/components/aeg-cancel-button/aeg-cancel-button.vue';

export const addMessageModal = 'add-message-modal';


const FORM_INITIAL_VALUES: AnnouncementFormData = {
    message: '',
    title: '',
    dateFrom: '',
    dateTo: '',
}

const ANNOUNCEMENT_VALIDATION_SCHEMA = toTypedSchema(
    yup.object({
        message: yup.string().required('Bericht is verplicht'),
        title: yup.string().required('Titel is verplicht'),
        dateFrom: yup.string().required('Begindatum is verplicht'),
        dateTo: yup.string().notRequired(),
    })
);


export default defineComponent({
    name: 'AegAddAnnouncement',
    components: {AegCancelButton, Dialog, Button, DatePicker, InputText, Textarea, Message },
    props: {
        visible: { type: Boolean, default: false },
        announcement: {
            type: Object as PropType<AnnouncementDto>,
            required: false
        }
    },
    emits: {
        'on-save': (announcement: AnnouncementDto | undefined) => true,
        'on-delete': (id: string | undefined) => true,
        'update:visible': (visible: boolean) => true,
    },
    setup: function (props, {emit}) {
        const visibleModel = computed({
            get: () => props.visible,
            set: (value) => emit('update:visible', value)
        });
        const announcementStore = useAnnouncementStore();
        const {isSavingAnnouncement, isDeletingAnnouncement} = storeToRefs(announcementStore);
        const announcementToEdit = ref<AnnouncementDto | undefined>(undefined);
        const {handleSubmit, defineField, errors, setValues, meta, resetForm} = useForm<AnnouncementFormData>({
            validationSchema: ANNOUNCEMENT_VALIDATION_SCHEMA,
            initialValues: FORM_INITIAL_VALUES
        });

        const [dateFrom, dateFromProps] = defineField('dateFrom');
        const [dateTo, dateToProps] = defineField('dateTo');
        const [title, titleProps] = defineField('title');
        const [message, messageProps] = defineField('message');

        const isNewMessage = computed(() => {
            return !announcementToEdit.value?.id;
        });

        const invalidForm = computed((): boolean => {
            return !meta.value.valid || isSavingAnnouncement.value;
        });
        const handleFormSubmit = async (formData: AnnouncementFormData) => {
            const announcementToSave = AnnouncementDto.fromJS({
                id: announcementToEdit.value?.id,
                severity: AnnouncementSeverity.Warning,
                from: DateUtil.formatForApi(formData.dateFrom, 'YYYY-MM-DD 00:00:00'),
                to: DateUtil.formatForApi(formData.dateTo, 'YYYY-MM-DD 00:00:00'),
                title: formData.title,
                message: formData.message,
            });

            const result = await announcementStore.saveAnnouncement(announcementToSave);
            onCloseModal();
            emit('on-save', result);
        }

        const onCloseModal = () => {
            visibleModel.value = false;
        }

        const removeAnnouncement = async () => {
            if (!announcementToEdit.value) return;
            await announcementStore.deleteAnnouncement(announcementToEdit.value?.id!)
            onCloseModal();
            emit('on-delete', announcementToEdit.value?.id!);
        }

        const announcementChanged = (newAnnouncement: AnnouncementDto | undefined) => {
            announcementToEdit.value = newAnnouncement;
            resetForm({
                values: newAnnouncement
                    ? {
                        message: newAnnouncement?.message,
                        title: newAnnouncement?.title,
                        dateFrom: newAnnouncement?.from,
                        dateTo: newAnnouncement?.to,
                    }
                    : FORM_INITIAL_VALUES
            });
        }

        const onSubmit = handleSubmit(handleFormSubmit);
        watch(() => props.announcement, announcementChanged, {immediate: true, deep: true});

        return {
            visibleModel,
            addMessageModal,
            dateFrom,
            dateFromProps,
            dateTo,
            dateToProps,
            title,
            titleProps,
            message,
            messageProps,
            errors,
            isNewMessage,
            invalidForm,
            isDeletingAnnouncement,
            onSubmit,
            onCloseModal,
            removeAnnouncement
        }
    }
});
